import React, { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../context/I18nContext';
import { 
  Accessibility, 
  X, 
  ZoomIn, 
  ZoomOut, 
  Moon, 
  Sun, 
  Type, 
  MousePointer2,
  Eye,
  Underline,
  Pause,
  RotateCcw
} from 'lucide-react';

const AccessibilityWidget = () => {
  const { locale } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('a11y_settings');
    return saved ? JSON.parse(saved) : {
      fontSize: 100,
      contrast: 'normal',
      highlightLinks: false,
      bigCursor: false,
      readableFont: false,
      pauseAnimations: false,
      textSpacing: false
    };
  });

  const texts = {
    it: {
      title: 'Accessibilità',
      fontSize: 'Dimensione Testo',
      contrast: 'Contrasto',
      normal: 'Normale',
      high: 'Alto',
      inverted: 'Invertito',
      highlightLinks: 'Evidenzia Link',
      bigCursor: 'Cursore Grande',
      readableFont: 'Font Leggibile',
      pauseAnimations: 'Pausa Animazioni',
      textSpacing: 'Spaziatura Testo',
      reset: 'Ripristina',
      close: 'Chiudi'
    },
    en: {
      title: 'Accessibility',
      fontSize: 'Font Size',
      contrast: 'Contrast',
      normal: 'Normal',
      high: 'High',
      inverted: 'Inverted',
      highlightLinks: 'Highlight Links',
      bigCursor: 'Big Cursor',
      readableFont: 'Readable Font',
      pauseAnimations: 'Pause Animations',
      textSpacing: 'Text Spacing',
      reset: 'Reset',
      close: 'Close'
    }
  };

  const t = texts[locale] || texts.en;

  // Apply settings to document
  useEffect(() => {
    const root = document.documentElement;
    
    // Font size
    root.style.fontSize = `${settings.fontSize}%`;
    
    // Contrast modes
    root.classList.remove('high-contrast', 'inverted-contrast');
    if (settings.contrast === 'high') {
      root.classList.add('high-contrast');
    } else if (settings.contrast === 'inverted') {
      root.classList.add('inverted-contrast');
    }
    
    // Highlight links
    root.classList.toggle('highlight-links', settings.highlightLinks);
    
    // Big cursor
    root.classList.toggle('big-cursor', settings.bigCursor);
    
    // Readable font
    root.classList.toggle('readable-font', settings.readableFont);
    
    // Pause animations
    root.classList.toggle('pause-animations', settings.pauseAnimations);
    
    // Text spacing
    root.classList.toggle('text-spacing', settings.textSpacing);
    
    // Save to localStorage
    localStorage.setItem('a11y_settings', JSON.stringify(settings));
  }, [settings]);

  const updateSetting = useCallback((key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetSettings = useCallback(() => {
    setSettings({
      fontSize: 100,
      contrast: 'normal',
      highlightLinks: false,
      bigCursor: false,
      readableFont: false,
      pauseAnimations: false,
      textSpacing: false
    });
  }, []);

  const increaseFontSize = () => {
    setSettings(prev => ({
      ...prev,
      fontSize: Math.min(prev.fontSize + 10, 150)
    }));
  };

  const decreaseFontSize = () => {
    setSettings(prev => ({
      ...prev,
      fontSize: Math.max(prev.fontSize - 10, 80)
    }));
  };

  return (
    <>
      {/* Floating Button - Right side */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed right-4 bottom-4 z-50 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 focus:outline-none focus:ring-4 focus:ring-blue-300"
        aria-label={t.title}
        data-testid="accessibility-widget-btn"
      >
        <Accessibility className="h-7 w-7" />
      </button>

      {/* Panel */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={(e) => e.target === e.currentTarget && setIsOpen(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            role="dialog"
            aria-labelledby="a11y-title"
            data-testid="accessibility-panel"
          >
            {/* Header */}
            <div className="sticky top-0 bg-blue-600 text-white p-4 rounded-t-2xl flex items-center justify-between">
              <h2 id="a11y-title" className="text-xl font-bold flex items-center gap-2">
                <Accessibility className="h-6 w-6" />
                {t.title}
              </h2>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-blue-500 rounded-lg transition-colors"
                aria-label={t.close}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Font Size */}
              <div className="bg-gray-50 rounded-xl p-4">
                <label className="font-medium text-gray-700 flex items-center gap-2 mb-3">
                  <Type className="h-5 w-5 text-blue-600" />
                  {t.fontSize}: {settings.fontSize}%
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={decreaseFontSize}
                    className="flex-1 py-3 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
                    aria-label="Decrease font size"
                  >
                    <ZoomOut className="h-5 w-5" />
                    A-
                  </button>
                  <button
                    onClick={increaseFontSize}
                    className="flex-1 py-3 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
                    aria-label="Increase font size"
                  >
                    <ZoomIn className="h-5 w-5" />
                    A+
                  </button>
                </div>
              </div>

              {/* Contrast */}
              <div className="bg-gray-50 rounded-xl p-4">
                <label className="font-medium text-gray-700 flex items-center gap-2 mb-3">
                  <Eye className="h-5 w-5 text-blue-600" />
                  {t.contrast}
                </label>
                <div className="flex gap-2">
                  {['normal', 'high', 'inverted'].map((mode) => (
                    <button
                      key={mode}
                      onClick={() => updateSetting('contrast', mode)}
                      className={`flex-1 py-2 px-3 rounded-lg border-2 transition-colors text-sm ${
                        settings.contrast === mode
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white hover:border-blue-300'
                      }`}
                    >
                      {mode === 'normal' && <Sun className="h-4 w-4 mx-auto mb-1" />}
                      {mode === 'high' && <Eye className="h-4 w-4 mx-auto mb-1" />}
                      {mode === 'inverted' && <Moon className="h-4 w-4 mx-auto mb-1" />}
                      {t[mode]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Toggle Options */}
              <div className="space-y-2">
                {[
                  { key: 'highlightLinks', icon: Underline, label: t.highlightLinks },
                  { key: 'bigCursor', icon: MousePointer2, label: t.bigCursor },
                  { key: 'readableFont', icon: Type, label: t.readableFont },
                  { key: 'pauseAnimations', icon: Pause, label: t.pauseAnimations },
                  { key: 'textSpacing', icon: Type, label: t.textSpacing }
                ].map(({ key, icon: Icon, label }) => (
                  <button
                    key={key}
                    onClick={() => updateSetting(key, !settings[key])}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                      settings[key]
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 bg-white hover:border-blue-300'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="flex-1 text-left font-medium">{label}</span>
                    <div className={`w-10 h-6 rounded-full transition-colors ${
                      settings[key] ? 'bg-blue-600' : 'bg-gray-300'
                    }`}>
                      <div className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform mt-0.5 ${
                        settings[key] ? 'translate-x-4.5 ml-4' : 'translate-x-0.5 ml-0.5'
                      }`} />
                    </div>
                  </button>
                ))}
              </div>

              {/* Reset Button */}
              <button
                onClick={resetSettings}
                className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
              >
                <RotateCcw className="h-5 w-5" />
                {t.reset}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Accessibility Styles */}
      <style>{`
        /* High Contrast Mode */
        .high-contrast {
          filter: contrast(1.4);
        }
        .high-contrast * {
          border-color: #000 !important;
        }
        
        /* Inverted Mode */
        .inverted-contrast {
          filter: invert(1) hue-rotate(180deg);
        }
        .inverted-contrast img,
        .inverted-contrast video,
        .inverted-contrast canvas {
          filter: invert(1) hue-rotate(180deg);
        }
        
        /* Highlight Links */
        .highlight-links a {
          background-color: #FFFF00 !important;
          color: #000 !important;
          text-decoration: underline !important;
          padding: 2px 4px !important;
        }
        
        /* Big Cursor */
        .big-cursor,
        .big-cursor * {
          cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24' fill='%23000'%3E%3Cpath d='M4 4l16 7-6 2-3 7z'/%3E%3C/svg%3E") 4 4, auto !important;
        }
        
        /* Readable Font */
        .readable-font,
        .readable-font * {
          font-family: Arial, Helvetica, sans-serif !important;
          letter-spacing: 0.05em !important;
        }
        
        /* Pause Animations */
        .pause-animations,
        .pause-animations * {
          animation-play-state: paused !important;
          transition: none !important;
        }
        
        /* Text Spacing */
        .text-spacing {
          word-spacing: 0.2em !important;
          letter-spacing: 0.1em !important;
          line-height: 1.8 !important;
        }
      `}</style>
    </>
  );
};

export default AccessibilityWidget;
